import {useFocusEffect} from '@react-navigation/native';
import {StackScreenProps} from '@react-navigation/stack';
import {Auth} from 'aws-amplify';
import {isValidPhoneNumber} from 'libphonenumber-js';
import {Box, Button, FormControl, Text, VStack, WarningOutlineIcon} from 'native-base';
import React, {useCallback, useContext, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ScrollView} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {PoweredBy} from '../components/PoweredBy';
import {KeyboardAvoidingView} from '../components/KeyboardAvoidingView';
import {MoneiWalletLogo} from '../components/svg/MoneiWalletLogo';
import {PhoneNumberInput} from '../components/PhoneNumberInput';
import {AuthContext} from '../lib/authContext';
import i18n from '../lib/i18n';
import {AuthNav, AuthNavParams} from './navigation';

export const LogInScreen: React.FC<StackScreenProps<AuthNavParams, AuthNav.LogInScreen>> = ({
  navigation
}) => {
  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: {errors}
  } = useForm({defaultValues: {phoneNumber: ''}});
  const {setAuthState} = useContext(AuthContext);
  const [isLoading, setLoading] = useState(false);

  useFocusEffect(
    useCallback(() => {
      reset();
    }, [])
  );

  const handleSignIn = async (phoneNumber: string) => {
    try {
      return await Auth.signIn(phoneNumber, undefined, {locale: i18n.locale});
    } catch (error: any) {
      let errorMessage = error.message?.replace('CreateAuthChallenge failed with error ', '');
      if (error.code === 'UserNotFoundException') {
        // await Auth.signUp(phoneNumber, getRandomString(32));
        // return await Auth.signIn(phoneNumber);
        errorMessage = i18n.t('userNotFound');
      }
      throw new Error(errorMessage);
    }
  };

  const onSubmit = async ({phoneNumber}: {phoneNumber: string}) => {
    setLoading(true);
    try {
      const user = await handleSignIn(phoneNumber);
      if (user.challengeName === 'CUSTOM_CHALLENGE') {
        setAuthState({
          currentUser: user,
          isAuthenticated: false
        });
        return navigation.navigate(AuthNav.ConfirmLogInScreen);
      }
      const currentUser = await Auth.currentAuthenticatedUser();
      setAuthState({currentUser, isAuthenticated: true});
    } catch (error: any) {
      if (error instanceof Error) {
        setError('phoneNumber', {type: 'manual', message: error.message});
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <KeyboardAvoidingView>
        <ScrollView
          contentContainerStyle={[t.flex, t.flex1, t.flexCol, t.justifyCenter]}
          keyboardShouldPersistTaps="handled">
          <Box style={[t.m3, t.mT32, {alignItems: 'center', justifyContent: 'center'}]}>
            <MoneiWalletLogo width="250" height="50" style={[t.m4]} />
          </Box>
          <Box style={[t.pX6, t.flex1]}>
            <Text style={[t.textCenter, t.mB4, t.textGray800]}>{i18n.t('enterPhoneNumber')}</Text>
            <VStack space={3}>
              <FormControl isInvalid={'phoneNumber' in errors}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: i18n.t('phoneNumberRequired'),
                    validate: (value) => isValidPhoneNumber(value) || i18n.t('phoneNumberInvalid')
                  }}
                  render={({field: {onChange, onBlur, value}}) => (
                    <PhoneNumberInput
                      autoFocus
                      countryCode={'ES'}
                      value={value}
                      onBlur={onBlur}
                      autoCorrect={false}
                      autoCapitalize="none"
                      onChangeText={(text) => onChange(text)}
                      blurOnSubmit={false}
                      returnKeyType="next"
                      onSubmitEditing={handleSubmit(onSubmit)}
                      placeholder={i18n.t('phoneNumber')}
                      textContentType="telephoneNumber"
                      keyboardType="phone-pad"
                    />
                  )}
                />
                <FormControl.ErrorMessage leftIcon={<WarningOutlineIcon size="xs" />}>
                  {errors?.phoneNumber?.message}
                </FormControl.ErrorMessage>
              </FormControl>
            </VStack>
          </Box>
          <PoweredBy />
        </ScrollView>
        <Box style={t.p6}>
          <Button isLoading={isLoading} onPress={handleSubmit(onSubmit)}>
            {i18n.t('continue')}
          </Button>
        </Box>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};
